import { Fragment, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import type { LoadedItem } from './LoadPos'

/**
 * The BOSS MultiPO colour × size grid (docs/BossMultiPO.md §4.5) and the product values above it.
 *
 * Two combined layouts, as legacy offered them:
 * - By PO: one column block per PO (sizes + total), side by side.
 * - By Size: one column block per size, holding each PO's quantity for that size, the way the
 *   legacy ProductListExplorer (TREE2) laid the grid out; per-PO totals follow the last size.
 * Per PO reuses the By PO layout with one PO at a time.
 */

export type GridLayout = 'bypo' | 'bysize'

/** One column group of the grid: a PO's quantities, or the store's on hand. */
export type ColumnGroup = {
    key: string
    label: string
    /** Short header for the narrow By Size columns. */
    short: string
    sub?: string
    locked?: boolean
    lockReason?: string
    /** Index into the PO tint palette; null for on hand. */
    tint: number | null
    editable: boolean
    qty: (item: LoadedItem, colorId: number, sizeId: number) => number
    /** Saves a cell (PO groups only). */
    onSave?: (item: LoadedItem, colorId: number, sizeId: number, oldQty: number, newQty: number) => void
    cellState?: (item: LoadedItem, colorId: number, sizeId: number) => CellState | undefined
}

export type CellState = { state: 'saving' | 'saved' | 'conflict' | 'error'; message?: string }

/** Legacy cycled four PO colours (ProductListExplorer.CreateBandsForTree2). */
export const PO_TINTS = 4

// ── Product values ───────────────────────────────────────────────────

export type FieldKey =
    | 'prodNo' | 'itemNumber' | 'styleCodes' | 'season' | 'grp1' | 'grp2' | 'grp3' | 'comment2' | 'pmNotes'
    | 'estLanded' | 'cost' | 'mecMatched' | 'retail' | 'margin' | 'qty' | 'inv' | 'extCost' | 'extRetail'

/** The legacy product row's columns, in legacy order and with legacy captions. */
export const PRODUCT_FIELDS: { key: FieldKey; label: string; title?: string }[] = [
    { key: 'prodNo', label: 'ProdNo' },
    { key: 'itemNumber', label: 'Item #', title: 'Supplier item number' },
    { key: 'styleCodes', label: 'Style Codes', title: 'Style numbers on the item card’s UPCs' },
    { key: 'season', label: 'Season', title: 'Item card season (Comment 1)' },
    { key: 'grp1', label: 'GRP1', title: 'Gender' },
    { key: 'grp2', label: 'GRP2', title: 'Department' },
    { key: 'grp3', label: 'GRP3', title: 'Sub-department' },
    { key: 'comment2', label: 'Comment2' },
    { key: 'pmNotes', label: 'PMNotes', title: 'Product manager notes (Comment 4)' },
    { key: 'estLanded', label: 'Est. Landed', title: 'Catalog estimated landed cost' },
    { key: 'cost', label: 'Cost', title: 'Catalog cost' },
    { key: 'mecMatched', label: 'MEC Matched', title: 'Retail is the MEC price-matched price' },
    { key: 'retail', label: 'Retail' },
    { key: 'margin', label: 'Margin', title: '(Retail − Est. Landed) ÷ Retail' },
    { key: 'qty', label: 'Qty', title: 'Units on the loaded POs' },
    { key: 'inv', label: 'Inv', title: 'On hand at this store' },
    { key: 'extCost', label: 'Ext. Cost', title: 'Qty × Cost' },
    { key: 'extRetail', label: 'Ext. Retail', title: 'Qty × Retail' },
]

/** Fields offered as drop-down product filters. */
export const SELECT_FILTER_FIELDS = ['season', 'grp1', 'grp2', 'grp3'] as const
export type SelectFilterField = typeof SELECT_FILTER_FIELDS[number]

const money = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' })
const pct = new Intl.NumberFormat('en-CA', { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })

export type ProductTotals = { qty: number; inv: number }

/** One product value as text, or null when there is nothing to show. */
export function fieldText(item: LoadedItem, key: FieldKey, totals: ProductTotals): string | null {
    const m = (v: number | null) => (v == null ? null : money.format(v))
    switch (key) {
        case 'prodNo': return item.prodNo || null
        case 'itemNumber': return item.itemNumber || null
        case 'styleCodes': return item.styleCodes || null
        case 'season': return item.season || null
        case 'grp1': return item.grp1 || null
        case 'grp2': return item.grp2 || null
        case 'grp3': return item.grp3 || null
        case 'comment2': return item.comment2 || null
        case 'pmNotes': return item.pmNotes || null
        case 'estLanded': return m(item.estLanded)
        case 'cost': return m(item.cost)
        case 'mecMatched': return item.mecMatched ? 'Yes' : 'No'
        case 'retail': return m(item.retail)
        case 'margin': {
            // Legacy: (retail − estimated landed) ÷ retail.
            if (!item.retail || item.estLanded == null) return null
            return pct.format((item.retail - item.estLanded) / item.retail)
        }
        case 'qty': return String(totals.qty)
        case 'inv': return String(totals.inv)
        case 'extCost': return item.cost == null ? null : money.format(totals.qty * item.cost)
        case 'extRetail': return item.retail == null ? null : money.format(totals.qty * item.retail)
    }
}

/** The text the style filter box searches: legacy searched ProdNo, description, item # and comments. */
export function searchText(item: LoadedItem): string {
    return [item.styleCode, item.prodNo, item.description, item.itemNumber, item.styleCodes, item.comment2, item.pmNotes]
        .filter(Boolean).join(' ').toLowerCase()
}

// ── Style block ──────────────────────────────────────────────────────

export function StyleBlock({ item, groups, layout, fields, totals }: {
    item: LoadedItem; groups: ColumnGroup[]; layout: GridLayout; fields: FieldKey[]; totals: ProductTotals
}) {
    const { sizes, colors } = item
    const shown = PRODUCT_FIELDS.filter(f => fields.includes(f.key))
    return (
        <div className="po-panel multipo-style">
            <div className={`multipo-style-head ${item.mecMatched ? 'mec' : ''}`}>
                <strong>{item.styleCode ?? '—'}</strong>
                <span>{item.description}</span>
                {item.retailMissing && (
                    <span className="multipo-warn" title="This catalog item has no retail price. BOSS stopped the whole load here; it is flagged instead.">
                        Retail not set
                    </span>
                )}
            </div>
            {shown.length > 0 && (
                <dl className="multipo-fields">
                    {shown.map(f => (
                        <div key={f.key} className={`multipo-field ${f.key === 'mecMatched' && item.mecMatched ? 'mec' : ''}`} title={f.title}>
                            <dt>{f.label}</dt>
                            <dd>{fieldText(item, f.key, totals) ?? '—'}</dd>
                        </div>
                    ))}
                </dl>
            )}
            {colors.length === 0 || sizes.length === 0 ? (
                <p className="po-grid-note">This item card has no colours or sizes set up.</p>
            ) : (
                <div className="po-grid-scroll multipo-grid-scroll">
                    <table className="po-cs-grid multipo-grid">
                        {layout === 'bypo' ? <ByPoHead item={item} groups={groups} /> : <BySizeHead item={item} groups={groups} />}
                        <tbody>
                            {colors.map((c, r) => (
                                <tr key={c.colorId}>
                                    <td className="po-cs-color">{c.name ?? '—'}</td>
                                    {layout === 'bypo'
                                        ? groups.map((g, gi) => {
                                            const row = sizes.map(s => g.qty(item, c.colorId, s.sizeId))
                                            return (
                                                <Fragment key={g.key}>
                                                    {sizes.map((s, si) => (
                                                        <QtyCell key={s.sizeId} group={g} item={item} colorId={c.colorId} sizeId={s.sizeId}
                                                            row={r} col={gi * (sizes.length + 1) + si} sizeAlt={si % 2 === 1} />
                                                    ))}
                                                    <td className={`po-cs-total ${tintClass(g)}`}>{sum(row) || ''}</td>
                                                </Fragment>
                                            )
                                        })
                                        : (
                                            <>
                                                {sizes.map((s, si) => groups.map((g, gi) => (
                                                    <QtyCell key={`${s.sizeId}-${g.key}`} group={g} item={item} colorId={c.colorId} sizeId={s.sizeId}
                                                        row={r} col={si * groups.length + gi} sizeAlt={si % 2 === 1} />
                                                )))}
                                                {groups.map(g => (
                                                    <td key={g.key} className={`po-cs-total ${tintClass(g)}`}>
                                                        {sum(sizes.map(s => g.qty(item, c.colorId, s.sizeId))) || ''}
                                                    </td>
                                                ))}
                                            </>
                                        )}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td className="po-cs-color">Total</td>
                                {layout === 'bypo'
                                    ? groups.map(g => {
                                        const cols = sizes.map(s => sum(colors.map(c => g.qty(item, c.colorId, s.sizeId))))
                                        return (
                                            <Fragment key={g.key}>
                                                {cols.map((q, si) => <td key={sizes[si].sizeId} className={tintClass(g)}>{q || ''}</td>)}
                                                <td className={tintClass(g)}>{sum(cols) || ''}</td>
                                            </Fragment>
                                        )
                                    })
                                    : (
                                        <>
                                            {sizes.map(s => groups.map(g => (
                                                <td key={`${s.sizeId}-${g.key}`} className={tintClass(g)}>
                                                    {sum(colors.map(c => g.qty(item, c.colorId, s.sizeId))) || ''}
                                                </td>
                                            )))}
                                            {groups.map(g => (
                                                <td key={g.key} className={tintClass(g)}>
                                                    {sum(sizes.map(s => sum(colors.map(c => g.qty(item, c.colorId, s.sizeId))))) || ''}
                                                </td>
                                            ))}
                                        </>
                                    )}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    )
}

function ByPoHead({ item, groups }: { item: LoadedItem; groups: ColumnGroup[] }) {
    return (
        <thead>
            <tr>
                <th className="po-cs-color" rowSpan={2}>Colour</th>
                {groups.map(g => (
                    <th key={g.key} colSpan={item.sizes.length + 1} className={`multipo-group-head ${tintClass(g)} ${g.locked ? 'locked' : ''}`}
                        title={g.locked ? g.lockReason : undefined}>
                        {g.locked ? '🔒 ' : ''}{g.label}{g.sub ? ` · ${g.sub}` : ''}
                    </th>
                ))}
            </tr>
            <tr>
                {groups.map(g => (
                    <Fragment key={g.key}>
                        {item.sizes.map((s, si) => <th key={s.sizeId} className={`${tintClass(g)} ${si % 2 === 1 ? 'size-alt' : ''}`}>{s.name ?? '—'}</th>)}
                        <th className={`po-cs-total ${tintClass(g)}`}>Total</th>
                    </Fragment>
                ))}
            </tr>
        </thead>
    )
}

function BySizeHead({ item, groups }: { item: LoadedItem; groups: ColumnGroup[] }) {
    return (
        <thead>
            <tr>
                <th className="po-cs-color" rowSpan={2}>Colour</th>
                {item.sizes.map((s, si) => (
                    <th key={s.sizeId} colSpan={groups.length} className={`multipo-size-head ${si % 2 === 1 ? 'size-alt' : ''}`}>{s.name ?? '—'}</th>
                ))}
                <th colSpan={groups.length} className="multipo-size-head po-cs-total">Total</th>
            </tr>
            <tr>
                {item.sizes.map((s, si) => groups.map(g => (
                    <th key={`${s.sizeId}-${g.key}`} className={`multipo-po-short ${tintClass(g)} ${si % 2 === 1 ? 'size-alt' : ''} ${g.locked ? 'locked' : ''}`}
                        title={`${g.label}${g.sub ? ` · ${g.sub}` : ''}${g.locked ? ` · ${g.lockReason}` : ''}`}>
                        {g.locked ? '🔒' : ''}{g.short}
                    </th>
                )))}
                {groups.map(g => (
                    <th key={g.key} className={`multipo-po-short po-cs-total ${tintClass(g)}`} title={g.label}>{g.short}</th>
                ))}
            </tr>
        </thead>
    )
}

const tintClass = (g: ColumnGroup) => (g.tint == null ? 'multipo-onhand' : `multipo-tint-${g.tint % PO_TINTS}`)
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0)

// ── Quantity cell ────────────────────────────────────────────────────

/** Most a single cell may hold. Legacy had no cap; this stops a slipped key ordering 100,000 units. */
export const MAX_CELL_QTY = 99999

/** Parses what was typed into a cell: blank is 0; anything else must be a whole number in range. */
export function parseQty(text: string): number | null {
    const t = text.trim()
    if (t === '') return 0
    if (!/^\d+$/.test(t)) return null
    const n = Number(t)
    return n <= MAX_CELL_QTY ? n : null
}

function QtyCell({ group, item, colorId, sizeId, row, col, sizeAlt }: {
    group: ColumnGroup; item: LoadedItem; colorId: number; sizeId: number; row: number; col: number; sizeAlt: boolean
}) {
    const value = group.qty(item, colorId, sizeId)
    const state = group.cellState?.(item, colorId, sizeId)
    const base = `${tintClass(group)} ${sizeAlt ? 'size-alt' : ''} ${value === 0 ? 'zero' : ''}`

    if (!group.editable || !group.onSave) {
        const cls = group.tint == null ? 'po-cs-inv' : `multipo-qty ${group.locked ? 'locked' : ''}`
        return <td className={`${cls} ${base}`}>{value || ''}</td>
    }
    return (
        <td className={`multipo-qty multipo-edit ${base} ${state ? `is-${state.state}` : ''}`} title={state?.message}>
            <QtyInput value={value} row={row} col={col}
                onCommit={n => group.onSave!(item, colorId, sizeId, value, n)} />
        </td>
    )
}

/** A borderless number box. Commits on blur; Enter and the arrow keys move like a spreadsheet. */
function QtyInput({ value, row, col, onCommit }: { value: number; row: number; col: number; onCommit: (n: number) => void }) {
    const [draft, setDraft] = useState(value ? String(value) : '')
    const [invalid, setInvalid] = useState(false)
    const focused = useRef(false)

    // Follow the saved value (a save, a conflict, a reload) unless the user is typing in this cell.
    useEffect(() => {
        if (!focused.current) setDraft(value ? String(value) : '')
    }, [value])

    const commit = () => {
        const n = parseQty(draft)
        if (n === null) {
            setInvalid(true)
            setDraft(value ? String(value) : '')
            return
        }
        setInvalid(false)
        setDraft(n ? String(n) : '')
        if (n !== value) onCommit(n)
    }

    const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        const input = e.currentTarget
        const atStart = input.selectionStart === 0 && (input.selectionEnd === 0 || input.selectionEnd === input.value.length)
        const atEnd = input.selectionEnd === input.value.length && (input.selectionStart === input.value.length || input.selectionStart === 0)
        let target: [number, number] | null = null
        if (e.key === 'Enter' || e.key === 'ArrowDown') target = [row + (e.shiftKey && e.key === 'Enter' ? -1 : 1), col]
        else if (e.key === 'ArrowUp') target = [row - 1, col]
        else if (e.key === 'ArrowLeft' && atStart) target = [row, col - 1]
        else if (e.key === 'ArrowRight' && atEnd) target = [row, col + 1]
        else if (e.key === 'Escape') {
            setDraft(value ? String(value) : '')
            setInvalid(false)
            input.blur()
            return
        }
        if (!target) return
        e.preventDefault()
        const next = findCell(input, target[0], target[1])
        if (next) next.focus()
        else if (e.key === 'Enter') input.blur()
    }

    return (
        <input className={`multipo-input ${invalid ? 'invalid' : ''}`} inputMode="numeric" value={draft}
            data-row={row} data-col={col}
            title={invalid ? `Enter a whole number from 0 to ${MAX_CELL_QTY}.` : undefined}
            onFocus={e => { focused.current = true; e.currentTarget.select() }}
            onBlur={() => { focused.current = false; commit() }}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onKeyDown} />
    )
}

/** The editable cell at (row, col) in the same table, skipping locked columns to the left or right. */
function findCell(from: HTMLInputElement, row: number, col: number): HTMLInputElement | null {
    const table = from.closest('table')
    if (!table || row < 0) return null
    const exact = table.querySelector<HTMLInputElement>(`input[data-row="${row}"][data-col="${col}"]`)
    if (exact) return exact
    // Same row, sideways: jump over locked or on-hand columns to the next editable one.
    if (row === Number(from.dataset.row)) {
        const step = col > Number(from.dataset.col) ? 1 : -1
        const inRow = [...table.querySelectorAll<HTMLInputElement>(`input[data-row="${row}"]`)]
            .map(i => ({ i, c: Number(i.dataset.col) }))
            .filter(x => (step > 0 ? x.c > col : x.c < col))
            .sort((a, b) => (step > 0 ? a.c - b.c : b.c - a.c))
        return inRow[0]?.i ?? null
    }
    return null
}
